import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';

const DEBOUNCE_MS = 300;

export default function ClientSelect({ clients, loading, error, fetchClients, onProceed }) {
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedInput(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputValue]);

  useEffect(() => {
    fetchClients(false);
  }, [fetchClients]);

  const filteredOptions = useMemo(() => {
    const q = debouncedInput.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (c.label || '').toLowerCase().includes(q));
  }, [clients, debouncedInput]);

  const handleProceed = useCallback(() => {
    if (selected) onProceed(selected);
  }, [selected, onProceed]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <Autocomplete
        options={filteredOptions}
        value={selected}
        onChange={(_, v) => setSelected(v)}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        getOptionLabel={(o) => o?.label || ''}
        isOptionEqualToValue={(a, b) => (a?.id != null && b?.id != null ? String(a.id) === String(b.id) : a === b)}
        filterOptions={(opts) => opts}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search client (company, city, state)"
            placeholder="Type to filter…"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={loading ? 'Loading…' : 'No clients match'}
      />

      {selected && (
        <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Selected client
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {selected.companyName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selected.address || '—'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Email:</strong> {selected.companyEmail || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Concerned person:</strong> {selected.concernedPersonName || '—'}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button variant="contained" disabled={!selected} onClick={handleProceed} sx={{ textTransform: 'none' }}>
          Proceed
        </Button>
      </Box>
    </Box>
  );
}
